import { useDocumentTitle } from "@/lib/use-document-title";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { CustomerLayout } from "@/components/CustomerLayout";
import { useCurrentMember } from "@/lib/data-hooks";
import { supabase } from "@/integrations/supabase/client";
import { Mail, MessageSquare } from "lucide-react";

function ProfilePage() {
  useDocumentTitle("Min profil — Lagamobilen Access");
  const member = useCurrentMember();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    phoneBrand: "", phoneModel: "", operator: "",
  });
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelErr, setCancelErr] = useState<string | null>(null);
  const [cancelDone, setCancelDone] = useState<{ endsAt: string; withinBinding: boolean } | null>(null);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [cancelFeedback, setCancelFeedback] = useState<string>("");
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const CANCEL_REASONS: { value: string; label: string }[] = [
    { value: "rabatt_reparation", label: "Jag tecknade främst medlemskapet för en rabatt eller reparation" },
    { value: "innehall_inte_intressant", label: "Utbildningarna och medlemskvällarna har inte varit tillräckligt intressanta" },
    { value: "annan_losning", label: "Jag har hittat en annan lösning för teknikhjälp" },
    { value: "ovrigt", label: "Övrigt (skriv gärna varför nedan)" },
  ];

  const bindingUntil = (member as any)?.binding_until ? new Date((member as any).binding_until) : null;
  const inBinding = !!bindingUntil && bindingUntil > new Date();
  const periodEnd = (member as any)?.current_period_end ? new Date((member as any).current_period_end) : null;
  const expectedEndDate = inBinding
    ? bindingUntil
    : periodEnd ?? null;
  const alreadyCancelled = !!(member as any)?.cancel_at && new Date((member as any).cancel_at) > new Date();

  async function confirmCancel() {
    if (!cancelReason) {
      setCancelErr("Välj en orsak innan du avslutar.");
      return;
    }
    if (cancelReason === "ovrigt" && cancelFeedback.trim().length < 3) {
      setCancelErr("Skriv gärna en kort förklaring i fältet nedan.");
      return;
    }
    setCancelBusy(true); setCancelErr(null);
    try {
      const { getStripeEnvironment } = await import("@/lib/stripe");
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        body: {
          environment: getStripeEnvironment(),
          reason: cancelReason,
          feedback: cancelFeedback.trim() || null,
        },
      });
      if (error) throw new Error(error.message || "Kunde inte avsluta");
      if ((data as any)?.error) throw new Error((data as any).error);
      setCancelDone({
        endsAt: (data as any).ends_at,
        withinBinding: !!(data as any).within_binding,
      });
    } catch (e: any) {
      setCancelErr(e.message ?? "Något gick fel");
    } finally {
      setCancelBusy(false);
    }
  }

  async function requestDataCopy() {
    setExporting(true); setExportErr(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error("Du måste vara inloggad");
      const url = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/gdpr-export`;
      const resp = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        const t = await resp.text(); throw new Error(t || `Fel: ${resp.status}`);
      }
      const blob = await resp.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl; a.download = `lagamobilen-access-datakopia.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
    } catch (e: any) {
      setExportErr(e.message ?? "Något gick fel");
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    if (!member) return;
    setForm({
      name: `${member.first_name} ${member.last_name}`,
      email: member.email,
      phone: member.phone ?? "",
      address: member.address ?? "",
      phoneBrand: member.phone_brand ?? "",
      phoneModel: member.phone_model ?? "",
      operator: member.operator ?? "",
    });
    setNotifyEmail(((member as any).notify_email ?? true) as boolean);
    setNotifySms(((member as any).notify_sms ?? false) as boolean);
  }, [member]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;
    setSaving(true);
    const [first_name, ...rest] = form.name.trim().split(/\s+/);
    const last_name = rest.join(" ") || member.last_name;
    await supabase
      .from("members")
      .update({
        first_name: first_name || member.first_name,
        last_name,
        phone: form.phone || null,
        address: form.address || null,
        phone_brand: form.phoneBrand || null,
        phone_model: form.phoneModel || null,
        operator: form.operator || null,
        notify_email: notifyEmail,
        notify_sms: notifySms,
      } as any)
      .eq("id", member.id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (!member) {
    return (
      <CustomerLayout title="Min profil">
        <div className="card-soft p-8 max-w-xl">
          <p className="text-sm text-muted-foreground">Ingen medlem är inloggad. <Link to="/access/login" className="text-primary underline">Logga in</Link> för att se din profil.</p>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout title="Min profil">
      <div className="max-w-4xl">
        <form onSubmit={save} className="card-soft p-6 lg:p-8 mb-8">
          <h2 className="mb-6">Personuppgifter</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div><label className="label-field">Namn</label><input className="input-field" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="label-field">E-post</label><input className="input-field" type="email" value={form.email} disabled title="Kontakta butiken om du behöver ändra primär e-post" /></div>
            <div><label className="label-field">Mobil</label><input className="input-field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className="label-field">Adress</label><input className="input-field" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div><label className="label-field">Telefonmärke</label><input className="input-field" value={form.phoneBrand} onChange={e => setForm({ ...form, phoneBrand: e.target.value })} /></div>
            <div><label className="label-field">Modell</label><input className="input-field" value={form.phoneModel} onChange={e => setForm({ ...form, phoneModel: e.target.value })} /></div>
            <div><label className="label-field">Operatör</label><input className="input-field" value={form.operator} onChange={e => setForm({ ...form, operator: e.target.value })} /></div>
          </div>

          <div className="mt-8 pt-6 border-t border-border">
            <h2 className="!text-lg mb-2">Notifieringar</h2>
            <p className="text-sm text-muted-foreground mb-4">Välj hur du vill få nyheter, erbjudanden och påminnelser om ditt medlemskap.</p>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" className="accent-[var(--color-primary)] size-4" checked={notifyEmail} onChange={e => setNotifyEmail(e.target.checked)} />
                <Mail size={16} className="text-muted-foreground" />
                <span className="text-sm">Få nyheter via e-post</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" className="accent-[var(--color-primary)] size-4" checked={notifySms} onChange={e => setNotifySms(e.target.checked)} />
                <MessageSquare size={16} className="text-muted-foreground" />
                <span className="text-sm">Få nyheter via SMS</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-8">
            <button className="btn-pill disabled:opacity-50" type="submit" disabled={saving}>{saving ? "Sparar…" : "Spara ändringar"}</button>
            {saved && <span className="text-sm text-[var(--color-success)] font-display">✓ Sparat!</span>}
          </div>
        </form>

        <div className="card-soft p-6 lg:p-8 mb-8">
          <h2 className="mb-3">GDPR – Hantera mina uppgifter</h2>
          <p className="text-sm text-muted-foreground mb-4">Du har rätt att begära ut, rätta eller radera de personuppgifter vi har om dig.</p>
          <div className="flex flex-wrap gap-3 items-center">
            <button onClick={requestDataCopy} disabled={exporting} className="btn-pill-outline disabled:opacity-50">
              {exporting ? "Förbereder…" : "Ladda ner datakopia"}
            </button>
            <Link to="/access/integritetspolicy" className="text-sm text-primary hover:underline">Läs integritetspolicy →</Link>
            <Link to="/access/villkor" className="text-sm text-primary hover:underline">Medlemsvillkor →</Link>
            <Link to="/access/angerratt" className="text-sm text-primary hover:underline">Ångerrätt →</Link>
          </div>
          {exportErr && <p className="text-xs text-destructive mt-3">{exportErr}</p>}
        </div>

        <div className="text-center">
          {alreadyCancelled ? (
            <p className="text-sm text-muted-foreground">
              Medlemskapet avslutas{" "}
              <strong>
                {new Date((member as any).cancel_at).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}
              </strong>.
            </p>
          ) : (
            <button onClick={() => { setCancelOpen(true); setCancelDone(null); setCancelErr(null); }} className="text-sm text-muted-foreground hover:text-destructive underline">
              Avsluta medlemskap
            </button>
          )}
        </div>

        {cancelOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !cancelBusy && setCancelOpen(false)}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              {cancelDone ? (
                <>
                  <h3 className="!text-xl font-display font-bold mb-2">Medlemskapet är avslutat</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Du har tillgång till alla förmåner fram till{" "}
                    <strong>{new Date(cancelDone.endsAt).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}</strong>.
                  </p>
                  {cancelDone.withinBinding && (
                    <p className="text-xs text-muted-foreground mb-6">
                      Eftersom du sade upp inom bindningstiden fortsätter månadsavgiften (99 kr) tills bindningstidens slut.
                    </p>
                  )}
                  <div className="flex justify-end">
                    <button onClick={() => setCancelOpen(false)} className="btn-pill">Stäng</button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="!text-xl font-display font-bold mb-2">Vill du avsluta?</h3>
                  {inBinding ? (
                    <div className="text-sm text-muted-foreground mb-4 space-y-2">
                      <p>
                        Du har <strong>3 månaders minsta löptid</strong> som löper till{" "}
                        <strong>{bindingUntil!.toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}</strong>.
                      </p>
                      <p>
                        Säger du upp nu schemaläggs avslutet till den dagen. Du fortsätter debiteras 99 kr/månad
                        fram till dess men har full tillgång till alla förmåner under tiden.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mb-4">
                      Medlemskapet avslutas vid din innevarande periods slut
                      {expectedEndDate && <> ({expectedEndDate.toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })})</>}.
                      Du behåller tillgång till alla förmåner fram tills dess.
                    </p>
                  )}

                  <div className="mb-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-display mb-2">
                      Varför vill du avsluta?
                    </div>
                    <div className="space-y-2">
                      {CANCEL_REASONS.map(r => (
                        <label key={r.value} className="flex items-start gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name="cancel-reason"
                            className="mt-1 accent-[var(--color-primary)]"
                            checked={cancelReason === r.value}
                            onChange={() => setCancelReason(r.value)}
                          />
                          <span>{r.label}</span>
                        </label>
                      ))}
                    </div>
                    <textarea
                      className="input-field mt-3 min-h-[80px]"
                      placeholder={cancelReason === "ovrigt" ? "Skriv din anledning här…" : "Vill du tillägga något? (frivilligt)"}
                      value={cancelFeedback}
                      onChange={e => setCancelFeedback(e.target.value)}
                      maxLength={2000}
                    />
                  </div>
                  {cancelErr && <p className="text-xs text-destructive mb-3">{cancelErr}</p>}
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setCancelOpen(false)} disabled={cancelBusy} className="btn-pill-outline">Ångra</button>
                    <button onClick={confirmCancel} disabled={cancelBusy} className="btn-pill bg-destructive hover:bg-destructive/90 disabled:opacity-60">
                      {cancelBusy ? "Avslutar…" : "Ja, avsluta"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
}

export default ProfilePage;
